import { City } from '@/data/cities';
import { CitySpecialty } from '@/data/citySpecialties';
import { CityLanguage } from '@/context/languages/languageData';
import { getSpecialtyNameForLanguage, getSpecialtyDescriptionForLanguage } from '@/utils/cityLanguage/getSpecialtySlugForLanguage';

export const generateSpecialtyServiceSchema = (
  city: City,
  specialty: CitySpecialty,
  language: CityLanguage = 'en'
) => {
  const specialtyName = getSpecialtyNameForLanguage(specialty, language);
  const specialtyDesc = getSpecialtyDescriptionForLanguage(specialty, language);
  const state = city.state_province;

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${specialtyName} in ${city.name}`,
    "description": specialtyDesc,
    "provider": {
      "@type": "Organization",
      "name": "Global Therapy Hub",
      "url": "https://psychotherapist.help"
    },
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city.name,
        "addressRegion": state || city.country,
        "addressCountry": city.country
      },
      ...(state && {
        "containedInPlace": {
          "@type": "State",
          "name": state
        }
      })
    },
    "availableChannel": [
      {
        "@type": "ServiceChannel",
        "serviceType": "In-Person Therapy"
      },
      {
        "@type": "ServiceChannel",
        "serviceType": "Online Therapy"
      }
    ]
  };
};

export const generateSpecialtyBreadcrumbSchema = (
  city: City,
  specialty: CitySpecialty,
  language: CityLanguage = 'en'
) => {
  const specialtyName = getSpecialtyNameForLanguage(specialty, language);
  const baseUrl = 'https://psychotherapist.help';
  const langPrefix = language === 'en' ? '' : `/${language}`;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${baseUrl}${langPrefix}/`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Cities",
        "item": `${baseUrl}${langPrefix}/cities`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": city.name,
        "item": `${baseUrl}${langPrefix}/city/${city.slug}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": specialtyName,
        "item": `${baseUrl}${langPrefix}/city/${city.slug}/${specialty.slugs[language] || specialty.slugs.en}`
      }
    ]
  };
};

export const generateSpecialtyFAQSchema = (faqs: { question: string; answer: string }[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const generateAllSpecialtySchemas = (
  city: City,
  specialty: CitySpecialty,
  faqs: { question: string; answer: string }[],
  language: CityLanguage = 'en'
) => {
  return [
    generateSpecialtyServiceSchema(city, specialty, language),
    generateSpecialtyBreadcrumbSchema(city, specialty, language),
    generateSpecialtyFAQSchema(faqs)
  ];
};
